import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RESOURCE_NAMES, getTutorialSteps, getShipDefinition, type FleetMissionType, type PlayerRace, type ShipId, type TutorialReward } from '@kaeliux/shared';
import {
    describeMissionAccountLock,
    extractScienceLevels,
    getMissionAccountLock,
    isTutorialStepComplete,
    tutorialProgressFromState,
    tutorialStepNeedsTeam,
} from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useTutorialStore } from '../store/tutorialStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { claimTutorialReward } from '../services/premium.service';
import { HttpError } from '../services/httpError';
import { useAccountStanding } from '../hooks/useAccountStanding';
import { useTutorialTeamProgress } from '../hooks/useTutorialTeamProgress';
import AccountLockNotice from './auth/AccountLockNotice';
import { Panel } from './ui/Panel';
import { Icon } from './ui/Icon';
import { Button } from './ui/Button';

const CARD_WIDTH = 384;
const CARD_MARGIN = 16;

/**
 * Tracks the viewport rect of the element tagged `data-tutorial-id={targetId}`.
 * Re-measures on resize/scroll, on the target's own size changes, and on any
 * DOM mutation (catches the target mounting after a route change, and
 * anything else shifting layout) — deliberately no polling interval or
 * requestAnimationFrame: Playwright's fake clock replays every timer tick
 * synchronously during `clock.runFor()`, so a large simulated time jump (a
 * science can take an hour-plus of game time) would turn even a coarse
 * interval into thousands of redundant re-measurements. Observers aren't
 * timers, so they cost nothing extra under a fake-clock jump.
 */
function useTutorialTargetRect(targetId: string | undefined, active: boolean): DOMRect | null {
    const [rect, setRect] = useState<DOMRect | null>(null);

    useEffect(() => {
        if (!active || !targetId) {
            setRect(null);
            return;
        }
        let scrolled = false;
        let observedElement: Element | null = null;
        const resizeObserver = new ResizeObserver(measure);

        function measure() {
            const el = document.querySelector(`[data-tutorial-id="${targetId}"]`);
            if (el !== observedElement) {
                if (observedElement) resizeObserver.unobserve(observedElement);
                if (el) resizeObserver.observe(el);
                observedElement = el;
            }
            if (el && !scrolled) {
                scrolled = true;
                el.scrollIntoView({ block: 'center' });
            }
            setRect(el ? el.getBoundingClientRect() : null);
        }

        measure();
        const mutationObserver = new MutationObserver(measure);
        mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [targetId, active]);

    return rect;
}

function cardPositionStyle(rect: DOMRect | null): CSSProperties {
    // maxHeight + internal scroll guarantees the card (and its Back/Next
    // buttons) always stay within the viewport — a tall spotlight target
    // squeezed between the top nav and bottom nav could otherwise push the
    // card's buttons off-screen and leave the player unable to advance.
    if (!rect) {
        return {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: `calc(100vh - ${2 * CARD_MARGIN}px)`,
            overflowY: 'auto',
        };
    }
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const placeBelow = spaceBelow > 220 || rect.top < 220;
    const left = Math.min(Math.max(rect.left, CARD_MARGIN), Math.max(CARD_MARGIN, viewportWidth - CARD_WIDTH - CARD_MARGIN));

    if (placeBelow) {
        const top = rect.bottom + CARD_MARGIN;
        return { top, left, maxHeight: Math.max(120, viewportHeight - top - CARD_MARGIN), overflowY: 'auto' };
    }
    const bottom = viewportHeight - rect.top + CARD_MARGIN;
    return { bottom, left, maxHeight: Math.max(120, rect.top - 2 * CARD_MARGIN), overflowY: 'auto' };
}

function formatReward(reward: TutorialReward, race: PlayerRace | undefined): string {
    switch (reward.type) {
        case 'resources': {
            const parts: string[] = [];
            if (reward.amounts.metal) parts.push(`${reward.amounts.metal.toLocaleString()} ${RESOURCE_NAMES.metal}`);
            if (reward.amounts.minerals) parts.push(`${reward.amounts.minerals.toLocaleString()} ${RESOURCE_NAMES.minerals}`);
            if (reward.amounts.gas) parts.push(`${reward.amounts.gas.toLocaleString()} ${RESOURCE_NAMES.gas}`);
            return parts.join(', ');
        }
        case 'ships':
            return reward.ships
                .map(({ shipId, quantity }) => {
                    const name = race ? getShipDefinition(race, shipId).name : 'Ship';
                    return `${quantity}× ${name}`;
                })
                .join(', ');
        case 'premium':
            return reward.label;
    }
}

export default function TutorialOverlay() {
    const { profile } = useAuthStore();
    const currentPlanet = usePlanetStore((s) => s.currentPlanet);
    const planets = usePlanetStore((s) => s.planets);
    const { sciences, shipUpgrades, colonizeFlightSent, premium } = usePlayerStore();
    const { isActive, stepIndex, disabled, refusedStepIndex, syncFromProfile, close, disable, next, back } = useTutorialStore();
    const navigate = useNavigate();
    const location = useLocation();
    const { openDialog } = useConfirmDialog();

    // tutorialStore clamps stepIndex to [0, steps.length - 1] for this same
    // race — defaults to Humans' path (always present) before the profile
    // (and so the player's actual race) has loaded.
    const steps = getTutorialSteps(profile?.race ?? 'humans');
    const step = steps[stepIndex]!;
    const team = useTutorialTeamProgress(step);
    const rect = useTutorialTargetRect(step.targetId, isActive);
    // One advance at a time: a second Next (or a Back) click while a reward
    // claim is in flight would otherwise move the step twice.
    const advancingRef = useRef(false);
    const [advancing, setAdvancing] = useState(false);

    // Whether the *current* step's real-world goal is satisfied right now —
    // always false for a manual step (per isTutorialStepComplete: it "never
    // self-completes", only the Next/Finish button advances it). Recomputed
    // every render (cheap: a couple of small object transforms + a switch),
    // not memoized. Ship upgrades are lazily loaded — only populated once
    // the player has visited the Tech Center (StarportPage's own loadShipUpgradeState call),
    // same lazy-load pattern `sciences` already follows for Sciences/Factory.
    // A `colonize` flight counts as sent while a colony ship is away, once a
    // second planet is owned or once the server recorded one (see
    // tutorialProgressFromState), so the overlay never has to fetch fleets.
    // The alliance steps read `team`, which is only loaded while one of them
    // is the current step. The server re-runs this same check before
    // crediting a reward or letting the player move past the step.
    const progress = tutorialProgressFromState({
        planet: currentPlanet,
        sciences: extractScienceLevels(sciences ?? undefined),
        shipUpgrades: (shipUpgrades ?? {}) as Partial<Record<ShipId, number>>,
        planetCount: planets.length,
        colonizeFlightSent,
        team,
    });
    const stepComplete = isTutorialStepComplete(step, progress);
    // Whether there's nothing left for the player to *do* on this step right
    // now — true for a manual step (there was never anything to wait for)
    // or a real step whose goal is already met. This is what gates the Next
    // button and the reward reveal, and (via the store sync below) the
    // character icon's pulse — deliberately broader than `stepComplete`
    // above, which the silent background-advance effect further down uses
    // instead specifically so a manual step never silently skips itself; it
    // always waits for an explicit Next/Finish click.
    const stepReady = step.completion.type === 'manual' || stepComplete;
    // A resources/ships reward is credited to the planet on screen, so Next
    // waits for that planet to load (a manual step is otherwise ready the
    // moment the overlay mounts, which is before AppShell's first loadPlanet).
    const rewardNeedsPlanet = step.reward !== undefined && step.reward.type !== 'premium';
    const canAdvance = stepReady && !advancing && (!rewardNeedsPlanet || currentPlanet !== null);

    // A step that asks for a flight this account can't send yet (issue #332:
    // guests can't colonize) says so, rather than leaving the player stuck.
    const { standing, now } = useAccountStanding();
    const stepMission = step.completion.type === 'fleetsSent' && !stepComplete
        ? step.completion.missionType as FleetMissionType
        : null;
    const stepMissionLock = stepMission ? getMissionAccountLock(stepMission, standing, now) : null;

    // Seeds step/disabled state (and auto-opens once for a brand-new player)
    // from the profile the moment it's available — a no-op after the first
    // successful call this session, see tutorialStore.
    useEffect(() => {
        if (profile) syncFromProfile(profile);
    }, [profile, syncFromProfile]);

    // Publishes stepReady so the character icon (in AppShell, which
    // doesn't otherwise have access to planet/science state) can react to it
    // without duplicating this whole computation.
    useEffect(() => {
        useTutorialStore.setState({ stepReady });
    }, [stepReady]);

    // Steer to the route each step teaches against — but only once per step,
    // and only while the popup is actually open (background tracking below
    // must never yank the player to a different page). Some destinations
    // redirect on their own when a precondition isn't met yet (e.g.
    // SciencesPage bounces to the Military zone with no Research Center built),
    // and re-navigating on every subsequent location change would fight that
    // redirect in an infinite loop. One attempt per step index is enough to
    // steer a player who's on the wrong page without overriding the app's
    // own navigation afterward.
    const navigatedForStepRef = useRef<number | null>(null);
    useEffect(() => {
        if (!isActive || !step.route) return;
        if (navigatedForStepRef.current === stepIndex) return;
        navigatedForStepRef.current = stepIndex;
        if (location.pathname !== step.route) navigate(step.route);
    }, [isActive, step, stepIndex, location.pathname, navigate]);

    // While the popup is *closed*, there's no Next button for the player to
    // click, so a satisfied step advances itself silently — that's what lets
    // the character icon's pulse reflect real progress made without ever
    // opening the tutorial. Deliberately keyed on `stepComplete`, not the
    // broader `stepReady` — a manual step is never "complete" on its own (it
    // has no real-world action to detect), only Next/Finish ever advances
    // one. Using `stepReady` here would treat every manual step as
    // instantly background-completable, which — since `isActive` starts
    // `false` and only flips `true` once the profile finishes loading — was
    // racing this effect ahead of the brand-new-player auto-open and
    // silently skipping the whole manual intro before the popup ever got a
    // chance to render.
    //
    // Exception: a step carrying a `reward` must not be skipped past unseen
    // — the player has to actually open the popup and lay eyes on what they
    // just earned before `next()` ever runs. The popup itself is never
    // forced open for this (see AppShell's pulsing character icon,
    // which also grows a "Commander, I have a message for you." tooltip
    // while a step is `stepReady`); the player opens it in their own time,
    // and the reward is still sitting there on this same step index when
    // they do. Reward-less steps (e.g. buildHangars) keep advancing
    // silently — there's nothing to collect, so holding those back would
    // just be noise.
    //
    // It also stands down on a step the server just refused to move past
    // (`refusedStepIndex`): the client judged it complete, the server did
    // not, and retrying on every revert would hammer the endpoint in a loop.
    // The refusal is forgotten when the player opens the tutorial, presses
    // Next or Back, or the step changes.
    useEffect(() => {
        if (isActive || disabled || !stepComplete || step.reward || refusedStepIndex === stepIndex) return;
        next();
    }, [isActive, disabled, stepComplete, step, stepIndex, refusedStepIndex, next]);

    // A refusal that lands while the popup is open (the player pressed Next
    // on a step the server doesn't consider complete) is explained the same
    // way a refused reward claim is. Announced once per refusal.
    const announcedRefusalRef = useRef<number | null>(null);
    useEffect(() => {
        if (refusedStepIndex === null) {
            announcedRefusalRef.current = null;
            return;
        }
        if (!isActive || refusedStepIndex !== stepIndex || announcedRefusalRef.current === refusedStepIndex) return;
        announcedRefusalRef.current = refusedStepIndex;
        showStepNotCompleteDialog();
    });

    if (!isActive) return null;

    const isLast = stepIndex === steps.length - 1;
    const rewardClaimed = premium?.claimedRewardSteps.includes(stepIndex) ?? false;

    // Advancing past a reward-bearing step claims its reward first
    // (server-side, idempotent per step — see `claimTutorialReward`).
    // Resources/ships land on the planet the step was completed on, and the
    // credited planet replaces `currentPlanet` unless the player switched
    // planets mid-claim; premium state goes straight into `usePlayerStore`
    // so tier-gated UI updates immediately.
    async function handleAdvance() {
        if (advancingRef.current) return;
        advancingRef.current = true;
        setAdvancing(true);
        try {
            await advance();
        } finally {
            advancingRef.current = false;
            setAdvancing(false);
        }
    }

    function showStepNotCompleteDialog() {
        openDialog({
            title: 'Step not complete yet',
            message: tutorialStepNeedsTeam(step)
                ? "This step isn't complete yet. Finish what it asks for, then press Next again."
                : "This step isn't complete on the planet you're viewing. Finish what it asks for there, then press Next again.",
            confirmLabel: 'Got it',
            cancelLabel: 'Close',
            onConfirm: () => { },
        });
    }

    async function advance() {
        const judgedOn = currentPlanet?.id;
        if (step.reward) {
            try {
                const { premium, planet } = await claimTutorialReward(stepIndex, judgedOn);
                usePlayerStore.setState({ premium });
                if (planet) usePlanetStore.getState().applyPlanet(planet);
            } catch (e) {
                // A failure must NOT advance past the step — doing so would
                // silently forfeit the reward, which the player can never
                // revisit. The server rejecting the step as not complete yet
                // isn't something a retry fixes, so that says what to do
                // instead; anything else offers a retry.
                console.error('Failed to claim tutorial reward:', e);
                if (e instanceof HttpError && e.status === 412) {
                    showStepNotCompleteDialog();
                    return;
                }
                openDialog({
                    title: "Couldn't collect your reward",
                    message: 'Something went wrong claiming your reward. Your progress is safe — try again in a moment.',
                    confirmLabel: 'Try again',
                    cancelLabel: 'Not now',
                    onConfirm: () => { void handleAdvance(); },
                });
                return;
            }
        }
        if (isLast) close(); else next(judgedOn);
    }

    function requestDisable() {
        openDialog({
            title: 'Disable tutorial?',
            message: "You can turn it back on anytime from the character icon — your progress is saved, so it'll pick up right where you left off.",
            confirmLabel: 'Disable tutorial',
            danger: true,
            onConfirm: disable,
        });
    }

    // The spotlighted target itself (when there is one) must stay click-
    // through so the player can directly interact with the real element
    // being taught (e.g. clicking the actual "Research" button — see the
    // Astronomy E2E test) — so "click outside to close" is built from bands
    // surrounding that cutout rather than one full-screen catcher. `box` is
    // the same padded rect the highlight glow itself is drawn at.
    const box = rect
        ? { top: rect.top - 8, left: rect.left - 8, right: rect.left + rect.width + 8, bottom: rect.top + rect.height + 8 }
        : null;

    return (
        <div className="fixed inset-0 z-[60] pointer-events-none">
            {box ? (
                <>
                    <div
                        className="absolute rounded-tile transition-all duration-200"
                        style={{
                            top: box.top,
                            left: box.left,
                            width: box.right - box.left,
                            height: box.bottom - box.top,
                            boxShadow: '0 0 0 9999px rgba(3, 7, 18, 0.75)',
                        }}
                    />
                    <div className="absolute pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: Math.max(0, box.top) }} onClick={close} />
                    <div className="absolute pointer-events-auto" style={{ top: box.bottom, left: 0, right: 0, bottom: 0 }} onClick={close} />
                    <div className="absolute pointer-events-auto" style={{ top: box.top, left: 0, width: Math.max(0, box.left), height: box.bottom - box.top }} onClick={close} />
                    <div className="absolute pointer-events-auto" style={{ top: box.top, left: box.right, right: 0, height: box.bottom - box.top }} onClick={close} />
                </>
            ) : (
                <div className="absolute inset-0 bg-black/75 pointer-events-auto" onClick={close} />
            )}

            <Panel
                variant="solid"
                padding="none"
                className="w-[calc(100%-2rem)] sm:w-96 pointer-events-auto absolute p-5 flex flex-col gap-3 shadow-e4"
                style={cardPositionStyle(rect)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="tutorial-step-title"
            >
                <div className="flex items-center justify-end text-micro text-content-muted">
                    <div className="flex items-center gap-3">
                        <button onClick={requestDisable} className="hover:text-content-secondary underline">
                            Disable tutorial
                        </button>
                        <button onClick={close} aria-label="Close" title="Close" className="hover:text-content-secondary text-body leading-none">
                            <Icon name="close" size={14} aria-hidden="true" />
                        </button>
                    </div>
                </div>
                <h2 id="tutorial-step-title" className="text-title text-content-primary">{step.title}</h2>
                <p className="text-body text-content-secondary">{stepComplete && step.completedBody ? step.completedBody : step.body}</p>
                {stepMission && stepMissionLock && (
                    <AccountLockNotice
                        showCreateAccount={stepMissionLock.reason === 'guest'}
                        modalZIndexClassName="z-[70]"
                        data-testid="tutorial-lock-notice"
                    >
                        {describeMissionAccountLock(stepMission, stepMissionLock, now)}
                    </AccountLockNotice>
                )}
                {stepReady && step.reward && !rewardClaimed && (
                    <p className="text-body text-status-warning flex items-center gap-1.5">
                        <Icon name="gift" size={16} aria-hidden="true" />
                        Reward: {formatReward(step.reward, profile?.race)}
                    </p>
                )}
                <div className="flex flex-col gap-1">
                    <div className="flex justify-between gap-3 mt-2">
                        <Button variant="secondary" onClick={back} disabled={stepIndex === 0 || advancing}>
                            Back
                        </Button>
                        <Button variant="primary" onClick={() => { void handleAdvance(); }} disabled={!canAdvance}>
                            {isLast ? 'Finish' : 'Next'}
                        </Button>
                    </div>
                    {!stepReady && (
                        <span className="text-content-muted text-micro text-right">Complete the step above to continue</span>
                    )}
                </div>
            </Panel>
        </div>
    );
}
